import { useTranslation } from 'react-i18next';
import StatusBadge from '../../../components/StatusBadge';
import StatusFlash from '../../../components/StatusFlash';
import { Episode, Project } from '../../../types';
import { getEpisodeProgressLabel } from '../utils/episodeProgress';

interface EpisodeProgressCardsProps {
  episodes: Episode[];
  project: Project;
  analysisStatus?: string;
  /** 点击卡片：打开该集「剧集原文」弹窗（不再跳转分集页）。 */
  onSelect: (episode: Episode) => void;
}

/**
 * 剧本信息页的「全集分集状态」卡片墙（一行约 6 个）。状态判定与分镜页侧栏共用
 * getEpisodeProgressLabel + StatusBadge，两处完全一致。点击卡片打开剧集原文弹窗。
 */
export default function EpisodeProgressCards({ episodes, project, analysisStatus, onSelect }: EpisodeProgressCardsProps) {
  const { t } = useTranslation('projectDetail');
  const sorted = [...episodes].sort((a, b) => a.index - b.index);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.6rem' }}>
      {sorted.map(ep => (
        <StatusFlash
          key={ep.id}
          status={ep.status}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(ep)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(ep); } }}
          style={{
            border: '1px solid var(--border)', borderRadius: '8px', padding: '0.6rem 0.7rem',
            background: 'var(--surface)', display: 'flex', flexDirection: 'column', gap: '0.4rem',
            transition: 'all 0.15s', height: '100%', boxSizing: 'border-box', cursor: 'pointer', color: 'inherit',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--theme-storyboard)'; e.currentTarget.style.background = 'color-mix(in srgb, var(--theme-storyboard) 8%, var(--surface))'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.85rem', fontWeight: 600, minWidth: 0 }}>
              {(ep.pendingCommentCount ?? 0) > 0 && (
                <span style={{ width: '7px', height: '7px', background: '#ef4444', borderRadius: '50%', flexShrink: 0 }} />
              )}
              EP{ep.index}
            </span>
            {/* 已完成（分镜全部产出）不再显示状态胶囊——无胶囊即代表已完成 */}
            {ep.status !== 'completed' && (
              <StatusBadge
                status={ep.status}
                label={getEpisodeProgressLabel(ep, project, analysisStatus, t)}
                style={{ fontSize: '0.68rem', padding: '0.12rem 0.45rem', whiteSpace: 'nowrap', flexShrink: 0 }}
              />
            )}
          </div>
          {(ep.totalShots ?? 0) > 0 && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-2)' }}>
              {t('episodes.shots', { completed: ep.completedShots, total: ep.totalShots })}
            </div>
          )}
        </StatusFlash>
      ))}
    </div>
  );
}
